import { ErrorMessage } from '@hookform/error-message';
import { OptionDefinitionCompressedTypeEnum } from '@jooxter/api';
import { BOOKING_OPTION_PREFIX } from '@jooxter/utils';
import { useCallback, useMemo } from 'react';
import { Controller, FieldValues, Message, UseFormRegister } from 'react-hook-form';
// eslint-disable-next-line import/named
import { CSSObjectWithLabel } from 'react-select';
import JxtCheckbox from '../JxtCheckbox';
import JxtInput from '../JxtInput';
import { JxtSelect } from '../Selectors';
import { IJxtBookingOptionsListItem } from './types';

// react-select truncates the selected value on one line by default
const wrappingSelectStyles = {
  singleValue: (base: CSSObjectWithLabel) => ({ ...base, whiteSpace: 'normal', overflowWrap: 'anywhere' as const }),
};

const JxtBookingOptionsListItem = ({
  option,
  register,
  getFieldState,
  formState,
  control,
}: IJxtBookingOptionsListItem) => {
  const selectOptions = useMemo(
    () => option.definition?.possibleValues?.map((v) => ({ label: v, value: v })) ?? [],
    [option]
  );
  const findSelectOption = useCallback(
    (value?: string) => selectOptions.find((o) => o.value === value),
    [selectOptions]
  );

  const getOptionalField = () => {
    if (!option.definition?.type || option.definition.type === OptionDefinitionCompressedTypeEnum.Checkbox) {
      return undefined;
    }

    // A disabled input or select cuts long values with no way to read them: show the whole value instead.
    // The field stays in the form values (fields are not unregistered on unmount), so the request is unchanged.
    if (!option.editable) {
      return (
        <Controller
          name={`options.${BOOKING_OPTION_PREFIX}-${option.id}.value`}
          control={control}
          render={({ field: { value } }) => (
            <div className="py-1 rounded-lg border border-neutral-10 p-2 text-body-s text-neutral-60 cursor-not-allowed whitespace-pre-line wrap-anywhere">
              {value}
            </div>
          )}
        />
      );
    }

    switch (option.definition?.type) {
      case OptionDefinitionCompressedTypeEnum.Text:
        return (
          <JxtInput
            name={`options.${BOOKING_OPTION_PREFIX}-${option.id}.value`}
            register={register as unknown as UseFormRegister<FieldValues>}
            getFieldState={getFieldState}
            formState={formState}
            className="flex-1 grow"
            showErrorMessages={false}
          />
        );
      case OptionDefinitionCompressedTypeEnum.Textarea:
        return (
          <JxtInput
            name={`options.${BOOKING_OPTION_PREFIX}-${option.id}.value`}
            register={register as unknown as UseFormRegister<FieldValues>}
            getFieldState={getFieldState}
            formState={formState}
            className="flex-1 grow"
            multiLines
            showErrorMessages={false}
          />
        );
      case OptionDefinitionCompressedTypeEnum.List:
        return (
          <div className="flex-1 grow">
            <Controller
              name={`options.${BOOKING_OPTION_PREFIX}-${option.id}.value`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <JxtSelect
                  options={selectOptions}
                  value={findSelectOption(value)}
                  handleSelection={(e) => e && onChange(e.value)}
                  styles={wrappingSelectStyles}
                />
              )}
            />
          </div>
        );

      default:
        return undefined;
    }
  };

  if (!option?.id) {
    return null;
  }

  const optionalField = getOptionalField();

  // The field gets the full width under its label, like the booking description, instead of half a row
  return (
    <div className="w-full flex flex-col justify-center gap-2 py-1">
      <Controller
        name={`options.${BOOKING_OPTION_PREFIX}-${option.id}.activated`}
        control={control}
        render={({ field: { value, onChange, name }, fieldState: { error } }) => (
          <>
            <JxtCheckbox
              name={name}
              value={value}
              onChange={onChange}
              label={option.definition?.name + (option.mandatory ? '*' : '')}
              disabled={!option.editable}
              multilineLabel
            />
            {error && (
              <div className="order-last">
                <ErrorMessage
                  name={name}
                  errors={{ [name]: error }}
                  render={({ message }: { message?: Message }) => <p className="text-red-100">{message}</p>}
                />
              </div>
            )}
          </>
        )}
      />
      {optionalField && <div className="min-w-0">{optionalField}</div>}
    </div>
  );
};

export default JxtBookingOptionsListItem;
